import { useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useSearchParams } from 'react-router-dom'
import { myTwinAtom } from 'state/my-twin'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useFetchThread, useFetchThreads } from 'hooks/use-email-responder'
import {
  SourceDocument,
  TwinContextSource,
  TwinNoteSummary,
  TwinResponderEmail,
  TwinResponderLabel,
  TwinResponderThread,
  TwinResponderThreadDraftStatus,
} from 'api/model'
import PaginationButtons from 'components/Gmail/PaginationButtons'
import styled from '@emotion/styled'
import { capitalizeOnlyFirstLetter, formatDateAsTime, formatTimestamp } from 'utils'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { authenticatedUserAtom, organizationFamily, toastAtom, twinResponderLabelsAtom } from 'state'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useActiveOrganization } from 'hooks/use-organization'
import {
  usersDeleteResponseDraft,
  usersGenerateTwinResponse,
  usersGenerateTwinResponseContent,
  usersUpdateTwinResponse,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { extract } from 'letterparser'
import ProfilePhoto from 'components/ProfilePhoto'
import { Letter } from 'react-letter'
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import { LoadingButton } from '@mui/lab'
import { getDebugProperties, useFetchDebugSettings } from 'hooks/use-debug'
import DebugSettings from 'components/Debug/DebugSettings'
import { enhanceDraftOpenAtom } from 'state/enhance-draft'
import AttachDocumentSelector from './AttachDocumentSelector'
import { DocumentType } from './AttachDocumentSelector'
import AttachedDocumentsList from './AttachedDocumentsList'
import { getSkipReasonText } from 'components/Gmail/ThreadList'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import EmailStatisticsModal from './EmailStatisticsModal'
import EnhanceDraftModal from './EnhanceDraftModal'
import { COLORS } from 'app-constants'
import { trackButtonClick } from 'analytics/events'

const LabelStack = styled(Stack)`
  padding: 5px;
  border-radius: 4px;
  background-color: #f5f5f5;
  justify-content: center;
  align-items: center;
  width: fit-content;
`

export const LabelTags = ({ labels }: { labels: string[] }) => {
  return (
    <Tooltip title={labels.join(', ')} enterTouchDelay={0}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        mx={2}
        maxHeight="32px"
        flex="0 0 100px"
        my={0}
        justifyContent="flex-start">
        {labels.slice(0, 1).map((label) => {
          return <LabelTag key={label} labelName={label} />
        })}
        {labels.length > 1 && (
          <LabelStack>
            <Typography
              variant="pS"
              sx={{
                color: COLORS.GRAY_3,
              }}>
              +{labels.length - 1}
            </Typography>
          </LabelStack>
        )}
      </Stack>
    </Tooltip>
  )
}

export const LabelTag = ({ labelName }: { labelName: string }) => {
  // update the labelName to not have the Category_ prefix
  if (labelName.startsWith('CATEGORY_')) {
    labelName = labelName.substring(9)
  }

  return (
    <LabelStack>
      <Typography
        variant="pXS"
        sx={{
          color: COLORS.GRAY_3,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}>
        {capitalizeOnlyFirstLetter(labelName)}
      </Typography>
    </LabelStack>
  )
}

const ThreadRow = ({
  thread,
  onPreviewThread,
}: {
  thread: TwinResponderThread
  onPreviewThread: (thread: TwinResponderThread) => void
}) => {
  const isMobile = useCheckMobileScreen()

  if (isMobile) {
    return (
      <Stack
        borderBottom={`1px solid ${COLORS.GRAY_7}`}
        py={1}
        onClick={() => {
          // track clicking on a thread
          trackButtonClick('email_thread_clicked')
          onPreviewThread(thread)
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            maxWidth="280px"
            minWidth="100px"
            textAlign="left"
            maxHeight="20px"
            variant="h6"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: COLORS.GRAY_0,
            }}>
            {thread.sender || thread.sender_email}
          </Typography>

          <Typography
            variant="pS"
            flex="0 0 96px"
            maxHeight="32px"
            textAlign="end"
            sx={{
              color: COLORS.GRAY_0,
            }}>
            {formatTimestamp(thread.date_received)}
          </Typography>
        </Stack>

        <Typography
          variant="pS"
          width="100%"
          mt={1}
          textAlign="left"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: COLORS.GRAY_0,
          }}>
          {thread.subject}
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack
      p={1}
      alignItems="center"
      direction="row"
      borderBottom={`1px solid ${COLORS.GRAY_8}`}
      onClick={() => {
        // track clicking on a thread
        trackButtonClick('email_thread_clicked')
        onPreviewThread(thread)
      }}
      sx={{
        width: '100%',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: COLORS.GRAY_8,
          borderRadius: '4px',
        },
      }}>
      <Typography
        flex="1 1 100px"
        maxWidth="280px"
        minWidth="100px"
        textAlign="left"
        maxHeight="20px"
        variant="h6"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: COLORS.GRAY_0,
        }}>
        {thread.sender || thread.sender_email}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} flex="1 0 388px" width="388px" maxHeight="32px">
        <LabelTags
          labels={thread.labels.map((label) => {
            if (label.startsWith('CATEGORY_')) {
              label = label.substring(9)
            }
            return capitalizeOnlyFirstLetter(label)
          })}
        />
        <Tooltip title={thread.subject} enterTouchDelay={0}>
          <Typography
            variant="pS"
            width="100%"
            px={0}
            textAlign="left"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: COLORS.GRAY_0,
            }}>
            {thread.subject}
          </Typography>
        </Tooltip>
      </Stack>
      <Typography
        variant="pS"
        ml={2}
        flex="0 0 96px"
        maxHeight="32px"
        textAlign="end"
        sx={{
          color: COLORS.GRAY_0,
        }}>
        {formatTimestamp(thread.date_received)}
      </Typography>
    </Stack>
  )
}

const SkipReasonCard = ({ reason }: { reason: string }) => {
  return (
    <Stack
      px={3}
      py="12px"
      border={`1px solid ${COLORS.SPECIAL_STROKE}`}
      borderRadius="8px"
      sx={{
        backgroundColor: COLORS.SPECIAL_FILL,
      }}>
      <Typography variant="pS">{`A draft was not generated for this email ${getSkipReasonText(
        reason
      )} You can force a draft by going into the email and clicking "Auto-draft a response".`}</Typography>
    </Stack>
  )
}

const ThreadSummary = ({ threadSummary }: { threadSummary?: string }) => {
  // TODO: add local storage logic to keep track of which emails have the summary collapsed
  const [isExpanded, setIsExpanded] = useState<boolean>(true)

  const onToggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const summaryStyle = {
    maxHeight: isExpanded ? '100%' : '0px',
    overflow: 'hidden',
    transition: 'max-height 0.2s ease-in-out', // Adjust timing and easing as desired
  }

  if (!threadSummary) {
    return null
  }

  return (
    <Stack
      px={3}
      py="12px"
      border={`1px solid ${COLORS.SPECIAL_STROKE}`}
      borderRadius="8px"
      sx={{
        backgroundColor: COLORS.SPECIAL_FILL,
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeIcon
            sx={{
              height: '16px',
              width: '16px',
            }}
          />

          <Typography variant="h7.25">AI generated summary of thread</Typography>
        </Stack>

        <Button onClick={onToggleExpand} variant="text" size="small">
          {isExpanded ? 'Collapse' : 'View'}
        </Button>
      </Stack>

      <Stack style={summaryStyle}>
        <Typography variant="pS">{threadSummary}</Typography>
      </Stack>
    </Stack>
  )
}

// this function is responsible for enhancing the auto drafted response
export const EnhanceDraft = ({
  onClose,
  threadId,
  twinId,
  setThread,
  setVerboseOutput,
}: {
  onClose: () => void
  threadId: string
  twinId: string
  setThread: React.Dispatch<React.SetStateAction<TwinResponderThread | null>>
  setVerboseOutput: React.Dispatch<React.SetStateAction<string>>
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const isMobile = useCheckMobileScreen()

  // loading state for re-draft click
  const [isRedrafting, setIsRedrafting] = useState<boolean>(false)

  // loading state for update click
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const [instructionText, setInstructionText] = useState<string>('')
  const onInstructionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInstructionText(e.target.value)
  }

  const onUpdateClick = async () => {
    if (!activeOrganization) {
      return
    }

    setIsUpdating(true)

    try {
      const instruction = {
        instruction: instructionText,
        note_ids: notes.map((n) => n.id),
        document_ids: sourceDocuments.map((d) => d.id),
      }

      const response = await usersUpdateTwinResponse(activeOrganization, twinId, {
        thread_id: threadId,
        content: {
          content: enhancedDraft,
        },
        with_instruction: shouldApplyInstructions ? instruction : undefined,
      })

      if (response.data.verbose_output) {
        setVerboseOutput(response.data.verbose_output)
      }

      // update the thread emails array with the newly drafted email
      setThread((prev) => {
        if (!prev) return null

        let updatedEmails: TwinResponderEmail[] = []
        if (prev.emails) {
          updatedEmails = prev.emails.map((e) => {
            if (e.id === response.data.drafted_email.id) {
              return response.data.drafted_email
            }
            return e
          })
        }

        return {
          ...prev,
          emails: updatedEmails,
        }
      })

      setToast({
        title: 'Successfully updated the draft',
        severity: 'success',
      })

      // should dismiss the enhanced draft component
      onClose()
    } catch (error) {
      setToast({
        title: 'Failed to update the draft',
        severity: 'error',
      })
    } finally {
      setIsUpdating(false)
    }
  }

  const [shouldApplyInstructions, setShouldApplyInstructions] = useState<boolean>(false)

  // local state keeping track of the documents that we're using to enhance the draft
  const [notes, setNotes] = useState<TwinNoteSummary[]>([])
  const [sourceDocuments, setSourceDocuments] = useState<SourceDocument[]>([])

  const [attachDocumentType, setAttachDocumentType] = useState<DocumentType | null>(null)

  const [enhancedDraft, setEnhancedDraft] = useState<string>('')
  const onRedraftClick = async () => {
    if (!activeOrganization) {
      return
    }

    setIsRedrafting(true)

    const instruction = {
      instruction: instructionText,
      note_ids: notes.map((n) => n.id),
      document_ids: sourceDocuments.map((d) => d.id),
    }

    try {
      const response = await usersGenerateTwinResponseContent(
        activeOrganization,
        twinId,
        {
          thread_id: threadId,
          use_finetune_model: true,
          content: enhancedDraft
            ? {
                content: enhancedDraft,
              }
            : undefined,
          instruction,
        },
        {
          verbose: true,
        }
      )
      setEnhancedDraft(response.data.drafted_content.content)

      if (response.data.verbose_output) {
        setVerboseOutput(response.data.verbose_output)
      }

      // track when a user generates a draft
      trackButtonClick('generate_draft_clicked')

      setToast({
        title: 'Successfully generated a new sample draft',
        severity: 'success',
      })
    } catch (Error) {
      setToast({
        title: 'Failed to generate a new sample draft',
        severity: 'error',
      })
    } finally {
      setIsRedrafting(false)
    }
  }

  const theme = useTheme()

  // TODO: need backend change for checking whether or not the user can generate a draft
  const isDraftDisabled = false

  return (
    <Stack
      position={isMobile ? 'relative' : 'fixed'}
      width={isMobile ? '100%' : '384px'}
      right={isMobile ? 'auto' : '0'}
      top={isMobile ? 'auto' : '0'}
      bottom={isMobile ? 'auto' : '0'}
      p={isMobile ? 0 : 2}
      alignItems="flex-start"
      gap={2}
      borderLeft={isMobile ? 'none' : '1px solid #ddd'}
      sx={{
        backgroundColor: COLORS.PURE_WHITE,
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeIcon
            sx={{
              height: '16px',
              width: '16px',
              color: COLORS.GRAY_2,
            }}
          />
          <Typography
            variant="pS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            Enhance response
          </Typography>
        </Stack>

        <IconButton onClick={onClose}>
          <CloseIcon
            sx={{
              height: '16px',
              width: '16px',
              fill: COLORS.GRAY_2,
            }}
          />
        </IconButton>
      </Stack>

      <Stack
        borderRadius="8px"
        px={2}
        pt={4}
        pb={2}
        gap={3}
        alignSelf="stretch"
        alignItems="flex-start"
        sx={{
          backgroundColor: COLORS.GRAY_8,
        }}>
        <TextFieldWithDarkMode
          required={true}
          className="has-label"
          theme={theme}
          label="Instructions"
          fullWidth
          multiline={true}
          isDarkBackdrop={true}
          placeholder="Respond more politely and refer to the following files"
          value={instructionText}
          onChange={onInstructionTextChange}
          sx={{
            backgroundColor: COLORS.PURE_WHITE,
          }}
        />

        <AttachDocumentSelector
          notes={notes}
          setNotes={setNotes}
          sourceDocuments={sourceDocuments}
          setSourceDocuments={setSourceDocuments}
          documentType={attachDocumentType}
          setDocumentType={setAttachDocumentType}
        />

        <Stack mt={-1} width="100%">
          <AttachedDocumentsList
            notes={notes}
            setNotes={setNotes}
            sourceDocuments={sourceDocuments}
            setSourceDocuments={setSourceDocuments}
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} width="100%">
          <Checkbox
            color="primary"
            checked={shouldApplyInstructions}
            onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              setShouldApplyInstructions(checked)
            }}
            sx={{
              marginRight: '0',
              padding: 0,
            }}
          />
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            Apply instructions to similar emails in the future
          </Typography>
        </Stack>
        <LoadingButton
          loading={isRedrafting}
          disabled={!instructionText || isDraftDisabled}
          variant="contained"
          onClick={onRedraftClick}
          size="small"
          sx={{
            color: COLORS.PURE_WHITE,
            alignSelf: 'flex-end',
          }}>
          Generate draft
        </LoadingButton>
      </Stack>

      {enhancedDraft && (
        <Stack
          width="100%"
          sx={{
            maxHeight: '50vh',
            overflowY: 'scroll',
          }}>
          <Stack
            borderRadius="8px"
            border={`1px solid ${COLORS.SPECIAL_STROKE}`}
            p={2}
            sx={{
              backgroundColor: COLORS.SPECIAL_FILL,
              whiteSpace: 'pre-line',
            }}>
            <Typography variant="pS">{enhancedDraft}</Typography>

            <LoadingButton
              loading={isUpdating}
              variant="contained"
              color="primary"
              size="small"
              onClick={onUpdateClick}
              sx={{
                mt: '12px',
                alignSelf: 'flex-end',
                width: 'fit-content',
              }}>
              Update
            </LoadingButton>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

const EmailCard = ({
  email,
  setPreviewedThread,
  twinId,
  setThread,
  setVerboseOutput,
  onDecrementDraftedCount,
  setThreads,
}: {
  email: TwinResponderEmail
  setPreviewedThread: React.Dispatch<React.SetStateAction<TwinResponderThread | null>>
  twinId: string
  setThread: React.Dispatch<React.SetStateAction<TwinResponderThread | null>>
  setVerboseOutput: React.Dispatch<React.SetStateAction<string>>
  onDecrementDraftedCount: () => void
  setThreads: React.Dispatch<React.SetStateAction<TwinResponderThread[]>>
}) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const setToast = useSetRecoilState(toastAtom)

  const [user] = useRecoilState(authenticatedUserAtom)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [isEnhancingDraft, setIsEnhancingDraft] = useRecoilState(enhanceDraftOpenAtom)

  const onDeleteDraftClick = async () => {
    if (!activeOrganization) {
      return
    }

    try {
      setIsSubmitting(true)
      await usersDeleteResponseDraft(activeOrganization, twinId, {
        draft_id: email.id,
      })
      setToast({ title: 'Draft deleted', severity: 'success' })

      setPreviewedThread((prev) => {
        if (!prev) return null
        return {
          ...prev,
          draft_status: TwinResponderThreadDraftStatus.not_drafted,
        }
      })

      setThread((prev) => {
        if (!prev) return null

        let updatedEmails: TwinResponderEmail[] = []
        if (prev.emails) {
          updatedEmails = prev.emails.filter((e) => e.id !== email.id)
        }

        return {
          ...prev,
          draft_status: TwinResponderThreadDraftStatus.not_drafted,
          emails: updatedEmails,
        }
      })

      // remove the email from the threads array
      setThreads((prev) => {
        if (!prev) return []
        return prev.filter((t) => t.id !== email.thread_id)
      })

      // we need to decrement the drafted count
      onDecrementDraftedCount()
    } catch {
      setToast({ title: 'Failed to delete draft', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  // TODO: currently the backend sometimes gives back " " instead of ""
  const { html, text } = extract(email.content === ' ' ? '' : email.content)

  const isDrafted = email.is_drafted_by_jelled
  const senderName = isDrafted ? user?.name : email.sender
  const senderEmail = isDrafted ? user?.email : email.sender_email

  return (
    <Stack borderRadius="8px">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <ProfilePhoto name={senderName} size="24px" />
          <Typography variant="h7.25">{senderName}</Typography>
          <Typography variant="pXS">{`<${senderEmail}>`}</Typography>
        </Stack>

        <Typography
          variant="pXS"
          pl="47px"
          mt={0}
          sx={{
            textAlign: 'center',
          }}>
          {formatDateAsTime(new Date(email.date_created))}
        </Typography>
      </Stack>

      <Stack
        mt={4}
        sx={{
          p: {
            wordWrap: 'break-word', // used to break long URLs into multiple lines
          },
        }}>
        {html === '' && text === '' ? ( // if both html and text are empty, then it's a plain text email
          <Typography
            sx={{
              whiteSpace: 'pre-line',
              wordWrap: 'break-word',
            }}>
            {email.content}
          </Typography>
        ) : (
          <Letter html={html || ''} text={text} />
        )}
      </Stack>

      {email.is_drafted_by_jelled && !isEnhancingDraft && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" mt={4}>
          <IconButton
            disabled={isSubmitting}
            color="secondary"
            onClick={onDeleteDraftClick}
            sx={{
              alignSelf: 'end',
              width: 'fit-content',
            }}>
            <DeleteOutlinedIcon
              sx={{
                fill: COLORS.GRAY_3,
              }}
            />
          </IconButton>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="outlined"
              startIcon={
                <AutoAwesomeIcon
                  sx={{
                    fill: COLORS.GRAY_0,
                    height: '16px',
                  }}
                />
              }
              onClick={() => {
                // track user starting to enhance draft
                trackButtonClick('enhancing_draft')
                setIsEnhancingDraft(true)
              }}
              sx={{
                color: COLORS.GRAY_0,
                px: '22px',
                width: '130px',
              }}>
              Enhance
            </Button>

            <Button
              variant="outlined"
              startIcon={
                <EditOutlinedIcon
                  sx={{
                    fill: COLORS.GRAY_0,
                    height: '16px',
                  }}
                />
              }
              onClick={() => {
                // open this url in a new window
                if (email.draft_url) {
                  // track clicking on edit draft
                  trackButtonClick('edit_draft_clicked')
                  window.open(email.draft_url, '_blank')
                }
              }}
              sx={{
                color: COLORS.GRAY_0,
                border: `1px solid ${COLORS.GRAY_5}`,
                '&:hover': {
                  borderWidth: '1px',
                },
                width: '98px',
              }}>
              Edit
            </Button>
          </Stack>
        </Stack>
      )}
      {isEnhancingDraft && !isMobile && (
        <EnhanceDraft
          onClose={() => setIsEnhancingDraft(false)}
          threadId={email.thread_id}
          twinId={twinId}
          setThread={setThread}
          setVerboseOutput={setVerboseOutput}
        />
      )}
      {isEnhancingDraft && isMobile && (
        <EnhanceDraftModal
          onClose={() => setIsEnhancingDraft(false)}
          setIsEnhancingDraft={setIsEnhancingDraft}
          email={email}
          twinId={twinId}
          setThread={setThread}
          setVerboseOutput={setVerboseOutput}
        />
      )}
    </Stack>
  )
}

const ThreadContent = ({
  labelId,
  onBackClick,
  previewedThread,
  setPreviewedThread,
  twinId,
  onIncrementDraftedCount,
  onDecrementDraftedCount,
  setThreads,
}: {
  labelId: string
  onBackClick: () => void
  previewedThread: TwinResponderThread
  setPreviewedThread: React.Dispatch<React.SetStateAction<TwinResponderThread | null>>
  twinId: string
  onIncrementDraftedCount: () => void
  onDecrementDraftedCount: () => void
  setThreads: React.Dispatch<React.SetStateAction<TwinResponderThread[]>>
}) => {
  const isMobile = useCheckMobileScreen()

  const twinResponderLabels = useRecoilValue(twinResponderLabelsAtom)
  const twinResponderLabel = twinResponderLabels.find((l: TwinResponderLabel) => l.id === labelId)

  const { thread, isLoading, setThread } = useFetchThread(previewedThread.id)

  const [verboseOutput, setVerboseOutput] = useState<string>('')
  const [contextSources, setContextSources] = useState<TwinContextSource[]>([])

  const shouldShowSummary = thread?.summary
  const getDisplayedEmails = () => {
    // if we are showing the summary, we should only show the last two emails
    // otherwise, we should show all the emails
    if (shouldShowSummary) {
      return thread.emails?.slice(thread.emails.length - 2) || []
    }
    return thread?.emails || []
  }

  // loop through emails and check if any are drafts
  const isDrafted = thread?.emails?.some((e) => e.is_drafted_by_jelled)
  const isDraftSent = thread?.draft_status === TwinResponderThreadDraftStatus.sent
  const isAutoDraftEnabled = !isDrafted && !isDraftSent
  const [isAutoDraftingResponse, setIsAutoDraftingResponse] = useState<boolean>(false)

  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const user = useRecoilValue(authenticatedUserAtom)
  const myTwin = useRecoilValue(myTwinAtom)

  const organization = useRecoilValue(organizationFamily(activeOrganization ?? ''))

  // used for debugging purposes
  const {
    isVerboseLogModalOpen,
    setIsVerboseLogModalOpen,
    contextType,
    setContextType,
    searchType,
    setSearchType,
    contextSize,
    setContextSize,
    maxContextSize,
    setMaxContextSize,
    relevance,
    setRelevance,
    temperature,
    setTemperature,
    maxTemperature,
    setMaxTemperature,
    modelType,
    setModelType,
    summaryModelType,
    setSummaryModelType,
    distance,
    setDistance,
    combineSearch,
    setCombineSearch,
    setMapReduceDepth,
    mapReduceDepth,
    conversationType,
    setConversationType,
    isStreamingToggled,
    setIsStreamingToggled,
    isMobiusToggled,
    setIsMobiusToggled,
  } = useFetchDebugSettings(organization)

  const onAutoDraftResponseClick = async () => {
    setIsAutoDraftingResponse(true)

    if (!activeOrganization || !thread) {
      return
    }

    try {
      const debugProperties = getDebugProperties(user?.is_system_admin ?? false, {
        contextSize: parseInt(contextSize),
        maxContextSize: parseInt(maxContextSize),
        relevance,
        searchType,
        contextType,
        temperature,
        maxTemperature,
        modelType,
        summaryModelType,
        distance,
        combineSearch,
        mapReduceDepth: parseInt(mapReduceDepth),
      })

      // update the current thread emails property with the newly drafted email
      const response = await usersGenerateTwinResponse(
        activeOrganization,
        twinId,
        {
          thread_id: thread.id,
          use_finetune_model: true,
          ...(user?.is_system_admin && { relevance_config: debugProperties }),
        },
        {
          verbose: user?.is_system_admin,
        }
      )

      // for the previewed thread, we just need to update draft_status
      setPreviewedThread((prev) => {
        if (!prev) return null

        return {
          ...prev,
          draft_status: TwinResponderThreadDraftStatus.drafted,
        }
      })

      // for the thread full, we want to update the emails array
      setThread((prev) => {
        if (!prev) return null

        let updatedEmails: TwinResponderEmail[] = []
        if (prev.emails) {
          updatedEmails = [...prev.emails, response.data.drafted_email]
        }

        return {
          ...prev,
          draft_status: TwinResponderThreadDraftStatus.drafted,
          emails: updatedEmails,
        }
      })

      // updated drafted count
      onIncrementDraftedCount()

      // used for debugging purposes
      if (response.data.verbose_output) {
        setVerboseOutput(response.data.verbose_output)
      }
      if (response.data.context_sources) {
        setContextSources(response.data.context_sources)
      }

      setToast({ title: 'Auto draft completed', severity: 'success' })
    } catch (error) {
      setToast({ title: 'Failed to auto generate draft', severity: 'error' })
    } finally {
      setIsAutoDraftingResponse(false)
    }
  }

  if (!twinResponderLabel) {
    return null
  }

  if (isLoading) {
    return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
  }

  return (
    <Stack spacing="10px">
      <Stack
        borderRadius="8px"
        border={`1px solid ${COLORS.GRAY_6}`}
        p={2}
        sx={{
          backgroundColor: COLORS.PURE_WHITE,
        }}>
        <Stack pl={isMobile ? 0 : 4} mt={3} spacing="20px">
          <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            spacing={2}
            flexWrap="wrap">
            <IconButton
              onClick={onBackClick}
              disableRipple={true}
              sx={{
                width: '24px',
                height: '24px',
                p: '4px',
                mr: 1,
              }}>
              <ArrowBackIcon />
            </IconButton>

            <Typography variant="pL">{previewedThread.subject}</Typography>

            <Stack direction="row" spacing={2} alignItems="flex-start">
              {previewedThread.labels.map((label) => {
                return <LabelTag key={label} labelName={label} />
              })}
            </Stack>
          </Stack>
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            The following is a preview of the email and does not contain any additional elements like images or
            attachments.
          </Typography>
        </Stack>
      </Stack>

      {thread?.reason && <SkipReasonCard reason={thread.reason} />}

      <ThreadSummary threadSummary={thread?.summary} />

      {getDisplayedEmails().map((email) => {
        return (
          <Stack
            key={email.id}
            borderRadius="8px"
            border={`1px solid ${COLORS.GRAY_6}`}
            p={2}
            sx={{
              backgroundColor: COLORS.PURE_WHITE,
            }}>
            <EmailCard
              email={email}
              setPreviewedThread={setPreviewedThread}
              twinId={twinId}
              setThread={setThread}
              setVerboseOutput={setVerboseOutput}
              onDecrementDraftedCount={onDecrementDraftedCount}
              setThreads={setThreads}
            />
          </Stack>
        )
      })}

      {isAutoDraftEnabled && (
        <LoadingButton
          loading={isAutoDraftingResponse}
          color="primary"
          variant="contained"
          onClick={onAutoDraftResponseClick}
          sx={{
            alignSelf: 'center',
            width: 'fit-content',
            mt: 4,
          }}>
          Generate draft
        </LoadingButton>
      )}

      <Stack mt={4}>
        <DebugSettings
          isMobiusToggled={isMobiusToggled}
          setIsMobiusToggled={setIsMobiusToggled}
          isSystemAdmin={user?.is_system_admin || false}
          twin={myTwin || undefined}
          modelType={modelType}
          setModelType={setModelType}
          summaryModelType={summaryModelType}
          setSummaryModelType={setSummaryModelType}
          temperature={temperature}
          setTemperature={setTemperature}
          maxTemperature={maxTemperature}
          setMaxTemperature={setMaxTemperature}
          relevance={relevance}
          setRelevance={setRelevance}
          contextType={contextType}
          setContextType={setContextType}
          contextSize={contextSize}
          setContextSize={setContextSize}
          maxContextSize={maxContextSize}
          setMaxContextSize={setMaxContextSize}
          searchType={searchType}
          setSearchType={setSearchType}
          distance={distance}
          setDistance={setDistance}
          combineSearch={combineSearch}
          setCombineSearch={setCombineSearch}
          verboseOutput={verboseOutput}
          setIsVerboseLogModalOpen={setIsVerboseLogModalOpen}
          isVerboseLogModalOpen={isVerboseLogModalOpen}
          contextSources={contextSources}
          mapReduceDepth={mapReduceDepth}
          setMapReduceDepth={setMapReduceDepth}
          conversationType={conversationType}
          setConversationType={setConversationType}
          isConversationSelectorDisabled={true}
          isDarkBackdrop={true}
          isConversationTypeHidden={true}
          isStreamingToggled={isStreamingToggled}
          setIsStreamingToggled={setIsStreamingToggled}
        />
      </Stack>
    </Stack>
  )
}

const Threads = ({
  labelId,
  onBackClick,
  onPreviewThread,
  previewedThread,
  setPreviewedThread,
}: {
  labelId: string
  onBackClick: () => void
  onPreviewThread: (thread: TwinResponderThread) => void
  previewedThread: TwinResponderThread | null
  setPreviewedThread: React.Dispatch<React.SetStateAction<TwinResponderThread | null>>
}) => {
  const isMobile = useCheckMobileScreen()
  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const [tabValue, setTabValue] = useState(0)
  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const [searchParams] = useSearchParams()
  const currentPageFromParams = Number(searchParams.get('currentPage')) || 0
  const [currentPage, setCurrentPage] = useState<number>(currentPageFromParams)

  const myTwin = useRecoilValue(myTwinAtom)

  let draftStatus = null
  if (tabValue === 0) {
    draftStatus = TwinResponderThreadDraftStatus.drafted
  } else if (tabValue === 1) {
    draftStatus = TwinResponderThreadDraftStatus.not_drafted
  } else {
    draftStatus = TwinResponderThreadDraftStatus.sent
  }

  const {
    threads,
    totalDraftedCount,
    totalNotDraftedCount,
    threadsPerPage,
    setThreadsPerPage,
    setTotalDraftedCount,
    setTotalNotDraftedCount,
    totalSentCount,
    setThreads,
  } = useFetchThreads(myTwin?.id ?? '', currentPage, '', draftStatus)

  // whenever we generate a new draft, we want to update both the total drafted count as well as the total not drafted count
  const onIncrementDraftedCount = () => {
    setTotalDraftedCount((prev) => prev + 1)
    setTotalNotDraftedCount((prev) => prev - 1)
  }

  // whenever we delete a draft, we want to decrement the drafted count
  const onDecrementDraftedCount = () => {
    setTotalDraftedCount((prev) => prev - 1)
    setTotalNotDraftedCount((prev) => prev + 1)
  }

  const filteredThreadsBySearchInput = threads.filter((thread) => {
    return (
      thread.subject.toLowerCase().includes(searchText.toLowerCase()) ||
      thread.sender.toLowerCase().includes(searchText.toLowerCase())
    )
  })

  // every time we change the tab value, we should reset the current page to 0
  useEffect(() => {
    setCurrentPage(0)
    searchParams.set('currentPage', '0')
  }, [tabValue, searchParams])

  // this should cause a new page of threads to be fetched
  const onPageChange = (page: number) => {
    setCurrentPage(page)
    searchParams.set('currentPage', page.toString())
  }

  const getThreadCount = () => {
    return tabValue === 0 ? totalDraftedCount : totalNotDraftedCount
  }

  const [isEmailStatisticsModalOpen, setIsEmailStatisticsModalOpen] = useState<boolean>(false)

  return (
    <Stack
      spacing={3}
      width="100%"
      sx={{
        maxWidth: isMobile ? '100%' : 'calc(100vw - 492px)',
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {!previewedThread && (
          <TextField
            placeholder="Search in mail"
            value={searchText}
            onChange={onSearchTextChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: COLORS.GRAY_3,
                      marginRight: '-10px',
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: isMobile ? '100%' : '640px',
              marginRight: isMobile ? 2 : 0,
              input: {
                color: 'secondary.main',
              },
              '& ::placeholder': {
                color: 'secondary.main',
              },
            }}
          />
        )}
        {!previewedThread && (
          <Button
            variant="outlined"
            startIcon={<AnalyticsOutlinedIcon />}
            onClick={() => {
              setIsEmailStatisticsModalOpen(true)
            }}
            sx={{
              background: COLORS.PURE_WHITE,
              color: COLORS.PURE_BLACK,
            }}>
            Statistics
          </Button>
        )}
      </Stack>

      {!previewedThread && (
        <Stack
          borderRadius="8px"
          border={`1px solid ${COLORS.GRAY_6}`}
          p={2}
          sx={{
            backgroundColor: COLORS.PURE_WHITE,
          }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            justifyContent="space-between">
            <Tabs
              orientation="horizontal"
              value={tabValue}
              onChange={onTabChange}
              textColor="secondary"
              sx={{
                '.MuiTabs-flexContainer': {
                  gap: isMobile ? 0 : 4,
                },
              }}>
              <Tab
                label="Drafted"
                icon={
                  <Stack
                    borderRadius="6px"
                    sx={{
                      padding: '0px 4px',
                      border:
                        tabValue === 0
                          ? `1px solid ${COLORS.JELLED_MID_BLUE_10_PERCENT}`
                          : `1px solid ${COLORS.GRAY_6}`,
                      '.MuiTab-iconWrapper': {
                        top: '2px',
                      },
                    }}>
                    <Typography
                      variant="pXS"
                      sx={{
                        color: tabValue === 0 ? COLORS.JELLED_PRIMARY_BLUE : COLORS.GRAY_3,
                      }}>
                      {totalDraftedCount}
                    </Typography>
                  </Stack>
                }
                iconPosition="end"
                sx={{
                  fontWeight: 500,
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 600,
                  },
                }}
              />
              <Tab
                label="Skipped"
                icon={
                  <Stack
                    borderRadius="6px"
                    sx={{
                      padding: '0px 4px',
                      border:
                        tabValue === 1
                          ? `1px solid ${COLORS.JELLED_MID_BLUE_10_PERCENT}`
                          : `1px solid ${COLORS.GRAY_6}`,
                    }}>
                    <Typography
                      variant="pXS"
                      sx={{
                        color: tabValue === 1 ? COLORS.JELLED_PRIMARY_BLUE : COLORS.GRAY_3,
                      }}>
                      {totalNotDraftedCount}
                    </Typography>
                  </Stack>
                }
                iconPosition="end"
                sx={{
                  fontWeight: 500,
                  '&.Mui-selected': {
                    color: COLORS.JELLED_PRIMARY_BLUE,
                    fontWeight: 600,
                  },
                }}
              />
              <Tab
                label="Sent"
                icon={
                  <Stack
                    borderRadius="6px"
                    sx={{
                      padding: '0px 4px',
                      border:
                        tabValue === 0
                          ? `1px solid ${COLORS.JELLED_MID_BLUE_10_PERCENT}`
                          : `1px solid ${COLORS.GRAY_6}`,
                      '.MuiTab-iconWrapper': {
                        top: '2px',
                      },
                    }}>
                    <Typography
                      variant="pXS"
                      sx={{
                        color: tabValue === 2 ? COLORS.JELLED_PRIMARY_BLUE : COLORS.GRAY_3,
                      }}>
                      {totalSentCount}
                    </Typography>
                  </Stack>
                }
                iconPosition="end"
                sx={{
                  fontWeight: 500,
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 600,
                  },
                }}
              />
            </Tabs>

            <PaginationButtons
              page={currentPage}
              onPageChange={onPageChange}
              count={getThreadCount()}
              threadsPerPage={threadsPerPage}
              onThreadsPerPageChange={setThreadsPerPage}
            />
          </Stack>

          <Divider
            sx={{
              my: 1,
            }}
          />

          {filteredThreadsBySearchInput.length > 0 ? (
            filteredThreadsBySearchInput.map((thread) => {
              return <ThreadRow key={thread.id} thread={thread} onPreviewThread={onPreviewThread} />
            })
          ) : (
            <Typography
              variant="pS"
              sx={{
                color: COLORS.GRAY_1,
              }}>
              No drafts to show yet.
            </Typography>
          )}
        </Stack>
      )}

      {previewedThread && (
        <ThreadContent
          labelId={labelId}
          previewedThread={previewedThread}
          setPreviewedThread={setPreviewedThread}
          onBackClick={onBackClick}
          twinId={myTwin?.id ?? ''}
          onIncrementDraftedCount={onIncrementDraftedCount}
          onDecrementDraftedCount={onDecrementDraftedCount}
          setThreads={setThreads}
        />
      )}
      {isEmailStatisticsModalOpen && <EmailStatisticsModal onClose={() => setIsEmailStatisticsModalOpen(false)} />}
    </Stack>
  )
}

export default Threads
